import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CalendarIcon,
  ChartBarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { attendanceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CheckInOut from '../components/CheckInOut';

const Attendance = () => {
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().split('T')[0].substring(0, 7)); // YYYY-MM format
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [groupedAttendance, setGroupedAttendance] = useState({});

  const { user, isEmployee, isManager, isAdmin } = useAuth();

  useEffect(() => {
    fetchAttendanceHistory();
  }, [selectedMonth, currentPage]);

  const fetchAttendanceHistory = async () => {
    try {
      setLoading(true);
      
      // Parse the selected month to get start and end dates
      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = new Date(year, month, 0).toISOString().split('T')[0]; // Last day of the month

      const response = await attendanceAPI.getHistory({
        month: selectedMonth, // Send month parameter instead of date range
        page: currentPage,
        limit: 31 // Increase limit to get all days in a month
      });
      
      // Transform the data to include proper calculations
      const attendanceData = response.data.attendance_records || response.data.attendance || response.data.data || response.data || [];
      const transformedHistory = Array.isArray(attendanceData) ? attendanceData.map(record => {
        // Since the data doesn't have events, calculate based on clock_in/clock_out
        const workedTime = calculateWorkedTimeFromRecord(record);
        return {
          ...record,
          calculated_worked_time: workedTime,
          events: [] // Ensure events array exists for compatibility
        };
      }) : [];
      
      // Group attendance by date
      const grouped = transformedHistory.reduce((acc, record) => {
        const date = record.date;
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(record);
        return acc;
      }, {});
      
      setAttendanceHistory(transformedHistory);
      setGroupedAttendance(grouped);
      const pagination = response.data.pagination || {};
      setTotalPages(pagination.total_pages || response.data.total_pages || response.data.totalPages || 1);

    } catch (error) {
      console.error('Error fetching attendance history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    return timeString.substring(0, 5); // HH:MM format
  };

  const calculateWorkedTimeFromRecord = (record) => {
    if (!record.clock_in || !record.clock_out) {
      return record.total_hours ? `${Math.floor(parseFloat(record.total_hours))}:${Math.round((parseFloat(record.total_hours) % 1) * 60).toString().padStart(2, '0')}` : '0:00';
    }
    
    const [inHour, inMin] = record.clock_in.split(':').map(Number);
    const [outHour, outMin] = record.clock_out.split(':').map(Number);
    
    const clockInTotal = inHour * 60 + inMin;
    const clockOutTotal = outHour * 60 + outMin;
    
    // Handle case where clock out is before clock in (crosses midnight)
    let diffMinutes;
    if (clockOutTotal < clockInTotal) {
      // Clock out is on the next day
      diffMinutes = (24 * 60 - clockInTotal) + clockOutTotal;
    } else {
      diffMinutes = clockOutTotal - clockInTotal;
    }
    
    // Subtract break time if present
    if (record.break_start && record.break_end) {
      const [breakStartHour, breakStartMin] = record.break_start.split(':').map(Number);
      const [breakEndHour, breakEndMin] = record.break_end.split(':').map(Number);
      
      const breakStartTotal = breakStartHour * 60 + breakStartMin;
      const breakEndTotal = breakEndHour * 60 + breakEndMin;
      
      if (breakEndTotal > breakStartTotal) {
        diffMinutes -= (breakEndTotal - breakStartTotal);
      }
    }
    
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateWorkedTimeFromEvents = (events) => {
    if (!events || events.length === 0) return '0:00';
    
    let totalMinutes = 0;
    let clockInTime = null;

    // Sort events by time to ensure proper order
    const sortedEvents = [...events].sort((a, b) => {
      const timeA = new Date(`2000-01-01 ${a.event_time}`);
      const timeB = new Date(`2000-01-01 ${b.event_time}`);
      return timeA - timeB;
    });

    for (const event of sortedEvents) {
      if (event.event_type === 'clock_in') {
        clockInTime = event.event_time;
      } else if (event.event_type === 'clock_out' && clockInTime) {
        const [inHour, inMin] = clockInTime.split(':').map(Number);
        const [outHour, outMin] = event.event_time.split(':').map(Number);
        
        const clockInTotal = inHour * 60 + inMin;
        const clockOutTotal = outHour * 60 + outMin;
        
        // Handle case where clock out is before clock in (crosses midnight)
        let diffMinutes;
        if (clockOutTotal < clockInTotal) {
          // Clock out is on the next day
          diffMinutes = (24 * 60 - clockInTotal) + clockOutTotal;
        } else {
          diffMinutes = clockOutTotal - clockInTotal;
        }
        
        totalMinutes += diffMinutes;
        clockInTime = null;
      }
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: 'bg-green-100', text: 'text-green-800', label: 'Present' },
      absent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Absent' },
      late: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Late' },
      'half-day': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Half Day' },
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track your daily attendance and view your history
          </p>
        </div>
      </div>

      {/* Check In/Out Widget */}
      <CheckInOut />

      {/* Attendance History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Attendance History</h2>
            <div className="flex items-center space-x-4">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <ChartBarIcon className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Worked Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : Object.keys(groupedAttendance).length > 0 ? (
                Object.entries(groupedAttendance)
                  .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA)) // Sort by date descending
                  .map(([date, records]) => {
                    // Calculate total worked time for the day
                    const totalWorkedMinutes = records.reduce((total, record) => {
                      if (record.calculated_worked_time && record.calculated_worked_time !== '0:00') {
                        const [hours, minutes] = record.calculated_worked_time.split(':').map(Number);
                        return total + (hours * 60) + minutes;
                      }
                      return total;
                    }, 0);
                    const totalHours = Math.floor(totalWorkedMinutes / 60);
                    const totalMinutes = totalWorkedMinutes % 60;
                    const totalWorkedTime = `${totalHours}:${totalMinutes.toString().padStart(2, '0')}`;

                    return records.map((record, index) => (
                      <tr key={`${date}-${record.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index === 0 ? (
                            <div className="font-medium">
                              {new Date(record.date).toLocaleDateString('en-GB', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                              {records.length > 1 && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Session {index + 1} of {records.length}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 pl-4">
                              Session {index + 1} of {records.length}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatTime(record.clock_in)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatTime(record.clock_out)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{record.calculated_worked_time}</div>
                          {index === records.length - 1 && records.length > 1 && (
                            <div className="text-xs font-medium text-blue-600 mt-1">
                              Total: {totalWorkedTime}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(record.status)}
                        </td>
                      </tr>
                    ));
                  })
                  .flat()
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No attendance records found for {selectedMonth}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary rounded-l-md"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary rounded-r-md"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
